import {
  CustomerPreference,
  DeprecatedTicketRightHolder,
  TicketRight,
  TicketTypePreference,
} from '../api-clients/api-types'
import { TicketRightWithDraft } from '../types'
import { getDateFromLocalDateString } from './date-utils'
import { isNullOrUndefined } from './property-util'
import {
  ticketTypeAdultText,
  ticketTypeSeniorCitizenText,
  ticketTypeStudentText,
} from '../hooks/use-customer-preferences'

const year = new Date().getFullYear()
const nextYear = year + 1
export const getYearForTicketRight = (ticketRight: TicketRight) =>
  parseInt(ticketRight.validFrom.split('-')[0], 10)

// Synced

export const hasSyncedToLisa = (ticketRight: TicketRight) =>
  ticketRight.legacySyncStatus?.isReadyToSync !== undefined &&
  ticketRight?.legacySyncStatus?.syncTimestamp !== null

export const isRejectedLisa = (ticketRight: TicketRight) => ticketRight.legacySyncStatus?.isReadyToSync === false

export const hasSyncedToESS = (ticketRight: TicketRight) =>
  ticketRight.essSyncStatus?.isReadyToSync !== undefined && ticketRight?.essSyncStatus?.syncTimestamp !== null

export const isRejectedESS = (ticketRight: TicketRight) => ticketRight.essSyncStatus?.isReadyToSync === false

const isValidForThisYear = (ticketRight: TicketRight) =>
  getYearForTicketRight(ticketRight) === year && hasSyncedToESS(ticketRight)

const isValidForNextYear = (ticketRight: TicketRight) =>
  getYearForTicketRight(ticketRight) === nextYear && hasSyncedToESS(ticketRight)

const isNotSyncedForThisYear = (ticketRight: TicketRight) =>
  getYearForTicketRight(ticketRight) === year &&
  !hasSyncedToESS(ticketRight) &&
  !isRejectedESS(ticketRight) &&
  isNullOrUndefined(ticketRight?.manualOrder)

const isNotSyncedForNextYear = (ticketRight: TicketRight) =>
  getYearForTicketRight(ticketRight) === nextYear &&
  !hasSyncedToESS(ticketRight) &&
  !isRejectedESS(ticketRight) &&
  isNullOrUndefined(ticketRight?.manualOrder)

const manualOrderIsSynced = (ticketRight: TicketRight) =>
  !isNullOrUndefined(ticketRight?.manualOrder) && hasSyncedToESS(ticketRight)
const manualOrderIsRejected = (ticketRight: TicketRight) =>
  !isNullOrUndefined(ticketRight?.manualOrder) && isRejectedESS(ticketRight)

const isValidYear = (ticketRight: TicketRight) =>
  (isValidForThisYear(ticketRight) || isValidForNextYear(ticketRight)) &&
  isNullOrUndefined(ticketRight?.manualOrder)
const isNotSyncedYear = (ticketRight: TicketRight) =>
  (isNotSyncedForThisYear(ticketRight) || isNotSyncedForNextYear(ticketRight)) &&
  isNullOrUndefined(ticketRight?.manualOrder)
const today = new Date()
const todayDate = new Date(today.getFullYear(), today.getMonth(), today.getDate())
const isExpiredSync = (ticketRight: TicketRight) =>
  getDateFromLocalDateString(ticketRight.validUntil).getFullYear() < year
const isExpired = (ticketRight: TicketRight) =>
  (todayDate > getDateFromLocalDateString(ticketRight.validUntil) && ticketRight.note !== 'OPPHØR') ||
  isExpiredSync(ticketRight)

const isSameTicketHolder = (
  ticketHolder1?: DeprecatedTicketRightHolder,
  ticketHolder2?: DeprecatedTicketRightHolder
) => {
  if (ticketHolder1 === null || ticketHolder2 === null) return true // We need to assume if no ticket right holder
  return ticketHolder1?.ticketHolderCode === ticketHolder2?.ticketHolderCode
}

// Remove duplicates

const removeDuplicate = (prev: TicketRight[], current: TicketRight) => {
  const yearlyDuplicate = prev.find(
    (it) =>
      getYearForTicketRight(it) === getYearForTicketRight(current) &&
      !current.manualOrder &&
      !it.manualOrder &&
      it.createdByRule?.name === current.createdByRule?.name &&
      it.ticketType.code === current.ticketType.code &&
      isSameTicketHolder(it.ticketRightsHolder, current.ticketRightsHolder)
  )
  const manualOrderDuplicate = prev.find(
    (it) => it.manualOrder && current.manualOrder && it.manualOrder.id === current.manualOrder.id
  )
  const duplicate = yearlyDuplicate || manualOrderDuplicate

  if (!duplicate) {
    prev.push(current)
  } else if (duplicate && current.ticketrightId > duplicate.ticketrightId) {
    const noDuplicates = prev.filter((it) => it.ticketrightId !== duplicate?.ticketrightId)
    noDuplicates.push(current)
    return noDuplicates
  }
  return prev
}

// Active

export const findActiveTicketRights = (ticketRights?: TicketRight[]) => {
  return ticketRights
    ?.sort((a, b) => a.ticketrightId - b.ticketrightId)
    .filter((it) => {
      return isValidYear(it) || manualOrderIsSynced(it)
    })
    .reduce(removeDuplicate, [])
    ?.filter((ticketRight) => ticketRight.note !== 'OPPHØR')
    .filter((it) => !isExpired(it))
}

// Ready to sync

export const findTicketRightsToSync = (ticketRights?: TicketRight[]): TicketRight[] => {
  return (
    ticketRights
      ?.sort((a, b) => a.ticketrightId - b.ticketrightId)
      .filter((it) => !isExpiredSync(it))
      .reduce(removeDuplicate, [])
      .filter((it) => {
        const syncManualOrder = !isNullOrUndefined(it?.manualOrder)
          ? !manualOrderIsSynced(it) && !manualOrderIsRejected(it)
          : false
        return isNotSyncedYear(it) || syncManualOrder
      }) || []
  )
}

// Draft

export const adjustTicketRightsWithDraft = (
  activeTicketRights?: TicketRight[],
  drafts?: TicketRight[]
): TicketRightWithDraft[] => {
  return (
    activeTicketRights?.map((ticketRight) => {
      const ticketRightDraft = drafts?.find(
        (draft) => getYearForTicketRight(ticketRight) === getYearForTicketRight(draft) && draft.note !== 'OPPHØR'
      )
      const ticketRightDraftOpphor = drafts?.find(
        (draft) => getYearForTicketRight(ticketRight) === getYearForTicketRight(draft) && draft.note === 'OPPHØR'
      )
      return {
        id: ticketRight.ticketrightId,
        ticketRight,
        draft: ticketRightDraft || ticketRightDraftOpphor,
      }
    }) || []
  )
}

// Temporary

export const createTemporaryTicketRight = (validForYear: number, employeeId: string, ticketholderCode: string) => {
  return {
    ticketrightId: -Math.random() * 100000,
    ticketHolderId: -1,
    employeeId: parseInt(employeeId, 10),
    ticketRightsHolder: {
      id: 1,
      ticketHolderCode: ticketholderCode,
    },
    validFrom: `${validForYear}-01-01`,
    validUntil: `${validForYear}-12-31`,
    ticketType: {
      code: 'NO',
      description: 'Draft',
      countryCode: 'NO',
    },
    errorMessage: '',
    note: '',
    ticketCreated: new Date(),
    calculationTimestamp: new Date(),
  } as TicketRight
}

// Check period dates

export const validateFromDateToDate = (fromDate?: Date, toDate?: Date): string | undefined => {
  if (fromDate && toDate) {
    if (fromDate >= toDate) {
      return 'Gyldig til dato kan ikke være før gyldig fra dato'
    }
    if (toDate <= new Date(new Date().toDateString())) {
      return 'Gyldig til dato kan ikke være før dagens dato'
    }
    if (toDate.getFullYear() < 2021) {
      return 'Gyldig til dato kan ikke være tidligere enn 01.01.2021'
    }
    return undefined
  }
  return 'Dato må være satt'
}

// check valid station pairs
export const validateStationPairs = (stationPairs: { fromStation?: string; toStation?: string }[]) => {
  let error: string | undefined

  stationPairs.forEach((pair) => {
    if (!pair.fromStation && pair.toStation) {
      error = `'Fra stasjon' kan ikke være tomt når 'Til stasjon' er definert`
    }

    if (pair.fromStation && !pair.toStation) {
      error = `'Til stasjon' kan ikke være tomt når 'Fra stasjon' er definert`
    }
  })
  return error
}

export const ticketTypeLabel = (p?: CustomerPreference): string => {
  switch (p?.ticketTypePreference) {
    case TicketTypePreference.SENIOR_CITIZEN:
      return ticketTypeSeniorCitizenText
    case TicketTypePreference.STUDENT:
      return ticketTypeStudentText
    default:
      return ticketTypeAdultText
  }
}
